<script setup>
import { get, onClickOutside, set } from '@vueuse/core'
import xImage from './xImage.vue'
import xTipsSimple from './xTipsSimple.vue'

const props = defineProps({
  list: {
    type: Array,
    default: null,
  },
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  async: {
    type: Boolean,
    default: false,
  },
  large: {
    type: Boolean,
    default: false,
  },
  currentClass: {
    type: [String, Object],
    default: '',
  },
  isCurrent: {
    type: Boolean,
    default: true,
  },
  dynamicLabel: {
    type: Boolean,
    default: false,
  },
  k: {
    type: String,
    default: 'label',
  },
  listClass: {
    type: [String, Array, Object],
    default: '',
  },
  itemClass: {
    type: [String, Array, Object],
    default: '',
  },
  maxHeight: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '---',
  },
  left: {
    type: Boolean,
    default: false,
  },
  labelClass: {
    type: String,
    default: '',
  },
  showArrow: {
    type: Boolean,
    default: true,
  },
})
const emits = defineEmits(['update:modelValue', 'select'])

const dom = ref(null)
const curData = ref({})
const hasList = computed(() => props.list?.length > (props.large ? 0 : 1))
const found = computed(() => props.list?.find(i => i.key === props.modelValue))

function close() {
  dom.value.open = false
}
function onClick(i) {
  if (i.disabled)
    return

  set(curData, i)
  emits('select', i.key)
  close()
}
function setFirst() {
  const [first] = props.list
  if (first)
    set(curData, first)
}

if (props.async) {
  watch([() => props.list, () => props.modelValue], () => {
    if (get(found))
      set(curData, get(found))
    else
      set(curData, {})
  })
}
else {
  watch(() => props.list, () => {
    if (!get(found))
      setFirst()

    if (props.dynamicLabel) {
      if (get(found)) {
        const { k } = props
        get(curData)[k] = get(found)[k]
      }
    }
  }, {
    immediate: true,
  })
}

watch(() => props.modelValue, (v) => {
  const o = props.list.find(i => i.key === v)
  if (o)
    set(curData, o)
}, {
  immediate: true,
})

watch(curData, ({ key }) => {
  emits('update:modelValue', key)
}, {
  immediate: true,
})

watch(() => props.isCurrent, (isCurrent) => {
  if (!isCurrent)
    setFirst()
})

onClickOutside(dom, close)
</script>

<template>
  <details
    v-if="list"
    ref="dom"
    class="relative inline-flex py-px font-normal leading-5 select-none group"
    :class="{ '!px-4': large }"
  >
    <summary class="flex items-center h-full outline-none cursor-pointer marker:hidden" :class="[{ 'pointer-events-none': !hasList }, currentClass]">
      <slot name="current" v-bind="curData">
        <xImage
          v-if="curData.icon"
          :src="curData.icon"
          :alt="curData[k]"
          class="w-4 h-4 me-2"
        />
        <span v-if="curData[k]" class="text-xs font-medium" :class="labelClass">
          {{ curData[k] }}
        </span>
        <span v-else class="text-textTertiary">{{ placeholder }}</span>
      </slot>
      <span v-if="hasList && showArrow" class="ms-2 group-open:rotate-180" :class="{ 'ms-auto': large }">
        <svgo-common-select-arrow class="w-2 h-2" />
      </span>
    </summary>
    <ul
      v-if="hasList"
      class="absolute px-1.5 z-10 mt-0.5 flex-col hidden w-full py-1 overflow-y-auto border rounded-md shadow-md has-scroller bg-fillPagePrimary group-open:flex top-full min-w-fit border-border"
      :class="[listClass, left ? 'left-0' : 'right-0']"
    >
      <li
        v-for="i in list"
        :key="i.key"
        class="flex items-center font-medium flex-shrink-0 text-xs min-h-[30px] rounded px-2 cursor-pointer hover:bg-pageTertiary"
        :class="[{ 'bg-pageTertiary': curData.key === i.key && isCurrent, 'min-h-[3rem] !px-4': large, 'text-textTertiary !cursor-no-drop hover:!bg-transparent': i.disabled }, itemClass]"
        @click="onClick(i)"
      >
        <slot name="item" v-bind="i">
          <xImage
            v-if="i.icon"
            :src="i.icon"
            :alt="i[k]"
            class="w-4 h-4 me-4"
          />
          <xTipsSimple v-if="i.tips">
            {{ i[k] }}
          </xTipsSimple>
          <template v-else>
            {{ i[k] }}
          </template>
        </slot>
      </li>
      <slot name="append" />
    </ul>
  </details>
</template>
